import { Button, DatePicker, Form, Input, InputNumber, message, Select, Space } from "antd";
import { useEffect, useState } from "react";
import { serverFetch } from "../../server";
import dayjs from "dayjs";

const Build = ({ progId, progName, build, onClose }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [item, setItem] = useState(null);
  const isEdit = !!build;

  useEffect(() => {
    if (build) {
      // Редактирование существующего билда
      // Парсим дату как есть, без учета часового пояса
      const createdOnValue = build.createdOn 
        ? dayjs(build.createdOn.replace('Z', '')) 
        : null;
      
      const formData = {
        ...build,
        createdOn: createdOnValue,
      };
      setItem({ ...build, createdOn: build.createdOn });
      form.setFieldsValue(formData);
    } else {
      // Создание нового билда
      const now = dayjs();
      const newBuild = {
        progId,
        v1: 1,
        v2: 0,
        v3: 0,
        v4: 1,
        status: 0,
        createdOn: now.format('YYYY-MM-DDTHH:mm:ss'),
        description: "",
        url: "",
      };
      setItem(newBuild);
      form.setFieldsValue({ ...newBuild, createdOn: now });
    }
  }, [build, progId, form]);

  const onValuesChange = (changedValues, allValues) => {
    setItem(prev => {
      const updated = { ...prev };
      
      // Обрабатываем дату отдельно - без учета часового пояса
      if (changedValues.createdOn) {
        updated.createdOn = changedValues.createdOn 
          ? changedValues.createdOn.format('YYYY-MM-DDTHH:mm:ss') 
          : null;
      }
      
      // Обновляем остальные поля
      Object.keys(changedValues).forEach(key => {
        if (key !== 'createdOn') {
          updated[key] = changedValues[key];
        }
      });
      
      return updated;
    });
  };

  const onSave = () => {
    form.validateFields()
      .then(() => {
        if (!item) return;

        const method = isEdit ? "PUT" : "POST";
        // Отправляем дату в формате без часового пояса
        const dataToSend = {
          ...item,
          progId,
          createdOn: item.createdOn || null,
        };

        setLoading(true);
        serverFetch("ProgBuild", {
          method,
          bodyData: dataToSend,
        })
          .then(() => {
            setLoading(false);
            message.success(isEdit ? "Дистрибутив обновлен" : "Дистрибутив создан");
            onClose(true); // Закрываем форму и обновляем список
          })
          .catch((e) => {
            setLoading(false);
            message.error(e.userMessage || "Ошибка сохранения дистрибутива");
          });
      })
      .catch((errorInfo) => {
        message.error("Заполните все обязательные поля");
      });
  };

  const onDelete = () => {
    if (!build) return;

    const version = `${build.v1}.${build.v2}.${build.v3}.${build.v4}`;
    
    if (!window.confirm(`Вы уверены, что хотите удалить дистрибутив ${version}?`)) {
      return;
    }

    setLoading(true);
    serverFetch(`ProgBuild/${build.progId}/${version}`, {
      method: "DELETE",
    })
      .then(() => {
        setLoading(false);
        message.success("Дистрибутив удален");
        onClose(true);
      })
      .catch((e) => {
        setLoading(false);
        message.error(e.userMessage || "Ошибка удаления дистрибутива");
      });
  };

  const onCancel = () => {
    onClose(false);
  };

  const statusOptions = [
    { value: 0, label: "Новый" },
    { value: 1, label: "Стабильный" },
    { value: 2, label: "Редактируется" },
  ];

  return (
    <>
      <div className="cloud-modal-overlay" onClick={onCancel} />
      <div className="cloud-modal-license-order-keys" style={{ maxWidth: "1200px" }}>
        <div className="cloud-modal-header">
          <h3>{isEdit ? "Редактирование дистрибутива" : "Создание дистрибутива"}</h3>
          <Button onClick={onCancel} type="text">×</Button>
        </div>
        <div className="cloud-modal-content">
          <Form
            form={form}
            onValuesChange={onValuesChange}
            labelCol={{ span: 6 }}
            wrapperCol={{ span: 18 }}
            colon={false}
            disabled={loading}
          >
            <Form.Item label="Программа">
              <Input value={progName} disabled />
            </Form.Item>

            <Form.Item label="Версия" required>
              <Space.Compact style={{ width: '100%' }}>
                <Form.Item
                  name="v1"
                  noStyle
                  rules={[{ required: true, message: 'Обязательно' }]}
                >
                  <InputNumber
                    min={0}
                    max={999}
                    placeholder="V1"
                    style={{ width: '25%' }}
                    disabled={isEdit}
                  />
                </Form.Item>
                <Form.Item
                  name="v2"
                  noStyle
                  rules={[{ required: true, message: 'Обязательно' }]}
                >
                  <InputNumber
                    min={0}
                    max={999}
                    placeholder="V2"
                    style={{ width: '25%' }}
                    disabled={isEdit}
                  />
                </Form.Item>
                <Form.Item
                  name="v3"
                  noStyle
                  rules={[{ required: true, message: 'Обязательно' }]}
                >
                  <InputNumber
                    min={0}
                    max={999}
                    placeholder="V3"
                    style={{ width: '25%' }}
                    disabled={isEdit}
                  />
                </Form.Item>
                <Form.Item
                  name="v4"
                  noStyle
                  rules={[{ required: true, message: 'Обязательно' }]}
                >
                  <InputNumber
                    min={0}
                    max={999}
                    placeholder="V4"
                    style={{ width: '25%' }}
                    disabled={isEdit}
                  />
                </Form.Item>
              </Space.Compact>
            </Form.Item>

            <Form.Item
              label="Дата публикации"
              name="createdOn"
            >
              <DatePicker
                showTime
                format="DD.MM.YYYY HH:mm"
                style={{ width: '100%' }}
              />
            </Form.Item>

            <Form.Item
              label="Статус"
              name="status"
              rules={[{ required: true, message: 'Выберите статус' }]}
            >
              <Select options={statusOptions} />
            </Form.Item>

            <Form.Item
              label="Описание"
              name="description"
            >
              <Input.TextArea 
                autoSize={{ minRows: 4 }} 
                placeholder="Описание дистрибутива" 
              />
            </Form.Item>

            <Form.Item
              label="URL"
              name="url"
            >
              <Input placeholder="https://..." />
            </Form.Item>

            <Form.Item wrapperCol={{ offset: 8, span: 16 }}>
              <Space>
                <Button type="primary" onClick={onSave} loading={loading}>
                  Сохранить
                </Button>
                <Button onClick={onCancel}>
                  Отмена
                </Button>
                {isEdit && (
                  <Button danger onClick={onDelete} loading={loading}>
                    Удалить
                  </Button>
                )}
              </Space>
            </Form.Item>
          </Form>
        </div>
      </div>
    </>
  );
};

export default Build;

